import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Clickable } from '@carvana/merch-components';
import { useState, useEffect, useRef, useContext } from 'react';
import type React from 'react';
import { usePagination } from '@/SearchResults/hooks';
import SearchBarEvents from '~/constants/SearchBarEvents';
import ArrowUp from '~/icons/ArrowUp';
import Close from '~/icons/Close';
import {
  KeywordSearchWrapper,
  SearchIconWrapper,
  SearchBarContainer,
  SearchIcon,
  StickyHeaderText
} from './KeywordSearch.styles';
import KeywordSuggestions from './KeywordSuggestions';

type OwnProps = {
  inputFocus?: boolean;
  isStickySearchbar?: boolean;
  setInputFocus: React.Dispatch<boolean>;
};

export default function KeywordSearch({
  inputFocus,
  isStickySearchbar,
  setInputFocus
}: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const {
    pagination: { totalMatchedInventory = 0 }
  } = usePagination();
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const [inputWrapperWidth, setInputWrapperWidth] = useState('100%');
  const [inputValue, setInputValue] = useState('');
  const [fetchValue, setFetchValue] = useState('');

  const { width: wrapperWidth } =
    inputWrapperRef.current?.getBoundingClientRect() || {};

  useEffect(() => {
    if (inputFocus && !isStickySearchbar) {
      trackEvent(SearchBarEvents.Focused);
    } else if (inputFocus && isStickySearchbar) {
      trackEvent(SearchBarEvents.Focused);
    }
  }, [inputFocus, isStickySearchbar, trackEvent]);

  const scrollToTop = () => {
    requestAnimationFrame(() =>
      window.scrollTo({
        top: document.documentElement.offsetTop,
        behavior: 'smooth'
      })
    );
  };

  useEffect(() => {
    if (inputWrapperRef.current) {
      setInputWrapperWidth(`${wrapperWidth?.toFixed(2)}px`);
    }
  }, [isStickySearchbar, wrapperWidth]);

  return (
    <div
      className="flex flex-row items-center justify-between w-full"
      ref={inputWrapperRef}
    >
      {isStickySearchbar && (
        <Clickable
          className="hidden self-center l:flex"
          data-analytics-id="sticky-scroll-top"
          data-qa="sticky-scroll-top"
          onClick={scrollToTop}
        >
          <ArrowUp className="mr-12" />
          <StickyHeaderText>Top</StickyHeaderText>
        </Clickable>
      )}
      <KeywordSearchWrapper
        inputFocus={inputFocus}
        isStickySearchbar={isStickySearchbar}
        width={inputWrapperWidth}
      >
        <SearchBarContainer isStickySearchbar={isStickySearchbar}>
          <SearchIconWrapper>
            <SearchIcon aria-label="Search" />
          </SearchIconWrapper>
          <KeywordSuggestions
            fetchValue={fetchValue}
            isStickySearchbar={isStickySearchbar}
            inputFocus={inputFocus}
            inputValue={inputValue}
            setFetchValue={setFetchValue}
            setInputFocus={setInputFocus}
            setInputValue={setInputValue}
          />
          {inputValue ? (
            <Clickable
              element={Close}
              className="fill-grey-3 mr-12 m:mr-20"
              data-analytics-id={SearchBarEvents.ClearedText}
              onClick={() => {
                setInputValue('');
                setFetchValue('');
              }}
            />
          ) : null}
        </SearchBarContainer>
      </KeywordSearchWrapper>

      {isStickySearchbar && (
        <p
          className="hidden mb-0 pl-16 self-center t-body-xs text-white l:block l:pl-0"
          data-qa="sticky-results-count"
        >
          Results: {totalMatchedInventory.toLocaleString()}
        </p>
      )}
    </div>
  );
}
